// src/components/themes/default/admin/BranchOverviewTab.tsx
"use client";

import {
  DollarSign,
  ShoppingBag,
  Users,
  Clock,
  Sparkles,
  PlusCircle,
  MessageSquare,
  TrendingUp,
} from "lucide-react";

interface BranchOverviewTabProps {
  branchInfo: any;
  productCount: number;
  ordersCount?: number;
  salesTotal?: number;
  pendingOrdersCount?: number;
  theme?: "dark" | "light";
  primaryColor?: string;
  onOpenProductModal?: () => void;
  setActiveTab?: (tab: string) => void;
}

export default function BranchOverviewTab({
  branchInfo,
  productCount = 0,
  ordersCount = 20,
  salesTotal = 23830,
  pendingOrdersCount = 6,
  theme = "dark",
  primaryColor = "#059669",
  onOpenProductModal,
  setActiveTab,
}: BranchOverviewTabProps) {
  const isLight = theme === "light";

  return (
    <div className="space-y-6 animate-fadeIn pb-10">
      {/* ১. টপ AI Executive Audit ব্যানার */}
      <div
        className={`p-5 rounded-3xl border flex flex-col md:flex-row md:items-center justify-between gap-4 ${
          isLight
            ? "bg-white border-slate-200 shadow-sm"
            : "bg-slate-900/90 border-slate-800"
        }`}
      >
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-indigo-600 flex flex-col items-center justify-center font-black text-white shrink-0 shadow-lg shadow-indigo-600/30">
            <span className="text-xl leading-none">94</span>
            <span className="text-[8px] uppercase tracking-widest text-indigo-200 mt-0.5">Health</span>
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-sm font-black text-white">Store Executive AI Audit: A+ (Excellent)</h2>
              <span className="px-2 py-0.5 rounded-full bg-indigo-500/20 text-indigo-400 text-[9px] font-black uppercase tracking-wider border border-indigo-500/30">
                REAL-TIME VERIFIED
              </span>
            </div>
            <p className="text-xs text-slate-400 mt-1">
              Store operating at peak efficiency with steady demand balance.
            </p>
          </div>
        </div>

        {/* Small Metric Badges */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="px-3 py-2 rounded-2xl bg-slate-950/60 border border-slate-800 text-center">
            <span className="block text-[8px] uppercase font-bold text-slate-400">Pricing Gain</span>
            <span className="text-xs font-mono font-black text-amber-400">+৳0</span>
          </div>
          <div className="px-3 py-2 rounded-2xl bg-slate-950/60 border border-slate-800 text-center">
            <span className="block text-[8px] uppercase font-bold text-slate-400">Stockouts</span>
            <span className="text-xs font-mono font-black text-emerald-400">0 items</span>
          </div>
          <div className="px-3 py-2 rounded-2xl bg-slate-950/60 border border-slate-800 text-center">
            <span className="block text-[8px] uppercase font-bold text-slate-400">Churn Risk</span>
            <span className="text-xs font-mono font-black text-indigo-400">৳0</span>
          </div>
          <div className="px-3 py-2 rounded-2xl bg-slate-950/60 border border-slate-800 text-center">
            <span className="block text-[8px] uppercase font-bold text-slate-400">Fraud Shield</span>
            <span className="text-xs font-mono font-black text-rose-400">0 flagged</span>
          </div>
        </div>
      </div>

      {/* ২. ৪টি প্রধান মেট্রিক্স কার্ড */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* TOTAL SALES */}
        <div
          className={`p-5 rounded-3xl border flex flex-col justify-between ${
            isLight ? "bg-white border-slate-200" : "bg-slate-900 border-slate-800"
          }`}
        >
          <div className="flex items-center justify-between text-slate-400 mb-2">
            <span className="text-[10px] font-black uppercase tracking-wider">TOTAL SALES</span>
            <div className="p-2 rounded-xl bg-indigo-500/10 text-indigo-400">
              <DollarSign className="w-4 h-4" />
            </div>
          </div>
          <div>
            <div className="text-2xl font-black font-mono">৳{salesTotal.toLocaleString()}</div>
            <p className="text-[10px] text-slate-500 font-bold mt-1">Live revenue</p>
          </div>
        </div>

        {/* TOTAL ORDERS */}
        <div
          className={`p-5 rounded-3xl border flex flex-col justify-between ${
            isLight ? "bg-white border-slate-200" : "bg-slate-900 border-slate-800"
          }`}
        >
          <div className="flex items-center justify-between text-slate-400 mb-2">
            <span className="text-[10px] font-black uppercase tracking-wider">TOTAL ORDERS</span>
            <div className="p-2 rounded-xl bg-blue-500/10 text-blue-400">
              <ShoppingBag className="w-4 h-4" />
            </div>
          </div>
          <div>
            <div className="text-2xl font-black font-mono">{ordersCount}</div>
            <p className="text-[10px] text-slate-500 font-bold mt-1">5 unique buyers</p>
          </div>
        </div>

        {/* ACTIVE VISITORS */}
        <div
          className={`p-5 rounded-3xl border flex flex-col justify-between ${
            isLight ? "bg-white border-slate-200" : "bg-slate-900 border-slate-800"
          }`}
        >
          <div className="flex items-center justify-between text-slate-400 mb-2">
            <span className="text-[10px] font-black uppercase tracking-wider">ACTIVE VISITORS</span>
            <div className="p-2 rounded-xl bg-purple-500/10 text-purple-400">
              <Users className="w-4 h-4" />
            </div>
          </div>
          <div>
            <div className="text-2xl font-black font-mono">0 Online</div>
            <p className="text-[10px] text-slate-500 font-bold mt-1">0 live sessions</p>
          </div>
        </div>

        {/* PENDING ORDERS */}
        <div
          className={`p-5 rounded-3xl border flex flex-col justify-between ${
            isLight ? "bg-white border-slate-200" : "bg-slate-900 border-slate-800"
          }`}
        >
          <div className="flex items-center justify-between text-slate-400 mb-2">
            <span className="text-[10px] font-black uppercase tracking-wider">PENDING ORDERS</span>
            <div className="p-2 rounded-xl bg-amber-500/10 text-amber-400">
              <Clock className="w-4 h-4" />
            </div>
          </div>
          <div>
            <div className="text-2xl font-black font-mono text-amber-400">{pendingOrdersCount}</div>
            <p className="text-[10px] text-slate-500 font-bold mt-1">Awaiting processing</p>
          </div>
        </div>
      </div>

      {/* ৩. চার্ট ও কুইক অ্যাকশন গ্রিড */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Revenue Velocity & 7-Day AI Trajectory */}
        <div
          className={`lg:col-span-2 p-6 rounded-3xl border flex flex-col justify-between ${
            isLight ? "bg-white border-slate-200" : "bg-slate-900 border-slate-800"
          }`}
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-black">Revenue Velocity & 7-Day AI Trajectory</h3>
              <p className="text-xs text-slate-400 mt-0.5">
                Solid line shows historical performance; dashed line indicates AI projected trend.
              </p>
            </div>
            <span className="text-xs px-3 py-1.5 rounded-xl bg-slate-950 border border-slate-800 font-bold text-slate-300">
              Gross Revenue (৳)
            </span>
          </div>

          {/* SVG Wave Line Graph */}
          <div className="h-64 w-full relative flex items-end pt-8 pb-4">
            <svg className="w-full h-full overflow-visible" viewBox="0 0 600 200" preserveAspectRatio="none">
              <defs>
                <linearGradient id="gradEmerald" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#10b981" stopOpacity="0.25" />
                  <stop offset="100%" stopColor="#10b981" stopOpacity="0.0" />
                </linearGradient>
              </defs>

              <path
                d="M 0 150 Q 80 180, 160 185 T 320 170 T 480 175 L 600 170 L 600 200 L 0 200 Z"
                fill="url(#gradEmerald)"
              />

              <path
                d="M 0 150 Q 80 180, 160 185 T 320 170 T 480 175 L 600 170"
                fill="none"
                stroke="#10b981"
                strokeWidth="3.5"
                strokeLinecap="round"
              />

              <circle cx="0" cy="150" r="4.5" fill="#10b981" />
              <circle cx="80" cy="180" r="4" fill="#0f172a" stroke="#10b981" strokeWidth="2" />
              <circle cx="160" cy="185" r="4" fill="#0f172a" stroke="#10b981" strokeWidth="2" />
              <circle cx="240" cy="178" r="4" fill="#0f172a" stroke="#10b981" strokeWidth="2" />
              <circle cx="320" cy="170" r="4" fill="#0f172a" stroke="#10b981" strokeWidth="2" />
              <circle cx="400" cy="173" r="4" fill="#0f172a" stroke="#10b981" strokeWidth="2" />
              <circle cx="480" cy="175" r="4" fill="#0f172a" stroke="#10b981" strokeWidth="2" />
              <circle cx="600" cy="170" r="4" fill="#0f172a" stroke="#10b981" strokeWidth="2" />
            </svg>
          </div>
        </div>

        {/* Right: Quick Actions */}
        <div
          className={`p-6 rounded-3xl border flex flex-col justify-between ${
            isLight ? "bg-white border-slate-200" : "bg-slate-900 border-slate-800"
          }`}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-500" />
              <h3 className="text-sm font-black">Quick Actions</h3>
            </div>
            <span className="text-[10px] text-slate-500 font-mono">26 Shortcuts</span>
          </div>

          <div className="space-y-2.5">
            <button
              onClick={onOpenProductModal}
              className="w-full flex items-center justify-between p-3 rounded-2xl bg-slate-950/60 hover:bg-slate-950 border border-slate-800/80 transition-all cursor-pointer group"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-indigo-500/10 text-indigo-400 group-hover:scale-105 transition-transform">
                  <PlusCircle className="w-4 h-4" />
                </div>
                <div className="text-left">
                  <span className="block text-xs font-black">Add New Product</span>
                  <span className="text-[10px] text-slate-500">Create item & stock</span>
                </div>
              </div>
              <span className="text-[10px] font-mono text-slate-400">MODAL &gt;</span>
            </button>

            <div className="w-full flex items-center justify-between p-3 rounded-2xl bg-slate-950/60 border border-slate-800/80">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-amber-500/10 text-amber-400">
                  <TrendingUp className="w-4 h-4" />
                </div>
                <div className="text-left">
                  <span className="block text-xs font-black">Dynamic Pricing Engine</span>
                  <span className="text-[10px] text-slate-500">Elasticity & margin optimizer</span>
                </div>
              </div>
              <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-indigo-500/20 text-indigo-400">AI</span>
            </div>

            <button
              onClick={() => setActiveTab && setActiveTab("orders")}
              className="w-full flex items-center justify-between p-3 rounded-2xl bg-slate-950/60 hover:bg-slate-950 border border-slate-800/80 transition-all cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-blue-500/10 text-blue-400">
                  <ShoppingBag className="w-4 h-4" />
                </div>
                <div className="text-left">
                  <span className="block text-xs font-black">Review Orders</span>
                  <span className="text-[10px] text-slate-500">Dispatch & process orders</span>
                </div>
              </div>
              <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-amber-500/20 text-amber-400">6 PENDING &gt;</span>
            </button>

            <button
              onClick={() => setActiveTab && setActiveTab("messages")}
              className="w-full flex items-center justify-between p-3 rounded-2xl bg-slate-950/60 hover:bg-slate-950 border border-slate-800/80 transition-all cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-emerald-500/10 text-emerald-400">
                  <MessageSquare className="w-4 h-4" />
                </div>
                <div className="text-left">
                  <span className="block text-xs font-black">Live Chat Panel</span>
                  <span className="text-[10px] text-slate-500">Customer real-time chat</span>
                </div>
              </div>
              <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-rose-500/20 text-rose-400">LIVE &gt;</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}